// src/components/common/AuthWrapper.tsx
import React, { useEffect, useState } from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
  useIsAuthenticated,
} from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "../../authConfig";
import { Navigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,

} from "@mui/material";
import MicrosoftIcon from "@mui/icons-material/Microsoft";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import BlockIcon from "@mui/icons-material/Block";
import apiClient from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import { getAuthToken, removeAuthToken, setAuthToken } from "../../api/client";
import { hasModulePermission } from "../../utils/permissionUtils";

export default function AuthWrapper({ children }: { children: React.ReactNode }) {
  const { user, setUser } = useAuth();
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = () => {
    setIsLoggingIn(true);
    instance.loginRedirect(loginRequest).catch(e => {
      setIsLoggingIn(false);
    });
  };

  const handleLogout = () => {
    removeAuthToken();
    setUser(null);
    instance.logoutRedirect();
  };

  useEffect(() => {
    const authenticateWithBackend = async () => {
      if (!isAuthenticated || !accounts.length || isAuthenticating) return;
      
      const existingToken = getAuthToken();
      if (existingToken && user) return;

      setIsAuthenticating(true);
      setError(null);

      try {
        const tokenResponse = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0]
        });

        const email = accounts[0].username.toLowerCase();
        if (!email.endsWith("@focaloid.com")) {
          setError("Access restricted to @focaloid.com users only.");
          setTimeout(() => instance.logoutRedirect(), 2000);
          return;
        }

        const response = await apiClient.post('/api/v1/auth/login', {
          access_token: tokenResponse.accessToken
        });

        const { access_token, user: userData } = response.data;
        
        setAuthToken(access_token);
        setUser(userData);

      } catch (err: any) {
        const errorMessage = err.response?.data?.detail || 'Failed to authenticate. Please try again.';
        setError(errorMessage);
      } finally {
        setIsAuthenticating(false);
      }
    };

    authenticateWithBackend();
  }, [isAuthenticated, accounts, instance, isAuthenticating, user, setUser]);

  // Show loading while MSAL is processing
 if (inProgress !== InteractionStatus.None || isAuthenticating) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #1e40af 100%)",
          gap: 2
        }}
      >
        <CircularProgress sx={{ color: "#fff" }} size={48} />
        <Typography sx={{ color: "#fff", fontSize: "1rem" }}>
          {isAuthenticating ? "Verifying your account..." : "Processing authentication..."}
        </Typography>
      </Box>
    );
  }

  // Show error if any
  if (error && isAuthenticated) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #1e40af 100%)",
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 400, textAlign: "center" }}>
          <BlockIcon sx={{ fontSize: 64, color: "#dc2626", mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Authentication Error
          </Typography>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button variant="contained" onClick={handleLogout}>
            Sign Out
          </Button>
        </Paper>
      </Box>
    );
  }

  // Show pending approval screen
  if (isAuthenticated && user && user.status === 'PENDING') {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #1e40af 100%)",
        }}
      >
        <Paper sx={{ p: 5, maxWidth: 480, textAlign: "center" }}>
          <HourglassEmptyIcon sx={{ fontSize: 72, color: "#d97706", mb: 2 }} />
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: "#0f172a" }}>
            Account Pending Approval
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: "#64748b" }}>
            Welcome, <strong>{user.name}</strong>! Your account has been created but is pending approval from the Director.
          </Typography>
          <Alert severity="info" sx={{ mb: 3, textAlign: "left" }}>
            <Typography variant="body2">
              • You have been assigned the <strong>Guest</strong> role<br />
              • The Director will review and approve your account<br />
              • You will receive access once approved
            </Typography>
          </Alert>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button 
              variant="outlined" 
              onClick={() => window.location.reload()}
            >
              Check Status
            </Button>
            <Button 
              variant="contained" 
              color="error"
              onClick={handleLogout}
            >
              Sign Out
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  // Show rejected screen
  if (isAuthenticated && user && user.status === 'REJECTED') {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #1e40af 100%)",
        }}
      >
        <Paper sx={{ p: 5, maxWidth: 480, textAlign: "center" }}>
          <BlockIcon sx={{ fontSize: 72, color: "#dc2626", mb: 2 }} />
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: "#0f172a" }}>
            Access Denied
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: "#64748b" }}>
            Your account access has been denied. Please contact the Director if you believe this is an error.
          </Typography>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleLogout}
          >
            Sign Out
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <>
      {/* Microsoft Login Page when not authenticated */}
      <UnauthenticatedTemplate>
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #1e40af 100%)",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
                radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.15) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(16, 185, 129, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 40% 40%, rgba(139, 92, 246, 0.08) 0%, transparent 40%)
              `,
              pointerEvents: "none",
            },
          }}
        >
          <Paper
            elevation={24}
            sx={{
              width: "100%",
              maxWidth: 440,
              mx: 2,
              p: 5,
              borderRadius: 4,
              background: "rgba(255, 255, 255, 0.98)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.4)",
              position: "relative",
              zIndex: 1,
            }}
          >
            {/* Logo / Brand */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: 3,
                  background: "linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 3,
                  boxShadow: "0 10px 30px -10px rgba(30, 64, 175, 0.5)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "2rem",
                    fontWeight: 800,
                    color: "#fff",
                    letterSpacing: "-0.05em",
                  }}
                >
                  F
                </Typography>
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "#0f172a",
                  mb: 1,
                }}
              >
                Focaloid Dashboard
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#64748b",
                  fontSize: "0.95rem",
                }}
              >
                Sign in to access your analytics
              </Typography>
            </Box>

            {/* Login Button */}
            <Button
              fullWidth
              variant="contained"
              onClick={handleLogin}
              disabled={isLoggingIn}
              startIcon={isLoggingIn ? <CircularProgress size={20} color="inherit" /> : <MicrosoftIcon />}
              sx={{
                py: 1.75,
                fontSize: "1rem",
                fontWeight: 600,
                background: "#2f2f2f",
                color: "#fff",
                borderRadius: 2,
                textTransform: "none",
                boxShadow: "0 4px 14px 0 rgba(0, 0, 0, 0.25)",
                transition: "all 0.2s ease",
                "&:hover": {
                  background: "#1a1a1a",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px 0 rgba(0, 0, 0, 0.35)",
                },
                "&:disabled": {
                  background: "#6b7280",
                  color: "#fff",
                },
              }}
            >
              {isLoggingIn ? "Redirecting..." : "Sign in with Microsoft"}
            </Button>

            {/* Footer */}
            <Box sx={{ mt: 4, textAlign: "center" }}>
              <Typography
                variant="caption"
                sx={{
                  color: "#94a3b8",
                  fontSize: "0.8rem",
                }}
              >
                {/* Access restricted to @focaloid.com accounts */}
              </Typography>
            </Box>
          </Paper>

          {/* Decorative elements */}
          <Box
            sx={{
              position: "absolute",
              bottom: 24,
              left: "50%",
              transform: "translateX(-50%)",
              color: "rgba(255, 255, 255, 0.5)",
              fontSize: "0.75rem",
              letterSpacing: "0.1em",
            }}
          >
            © {new Date().getFullYear()} Focaloid Technologies
          </Box>
        </Box>
      </UnauthenticatedTemplate>

      {/* Show protected children when authenticated */}
      <AuthenticatedTemplate>
          {(() => {
            if (!user) return null;
            if (user.status !== "APPROVED") return null;

            // Priority mapping: first route with read permission wins.
            const routePriority: Array<{ module: string; path: string }> = [
              { module: "Dashboard", path: "/" },
              { module: "Po Analysis", path: "/po-analysis" },
              { module: "Projects", path: "/projects/details" },
              { module: "Purchase Orders", path: "/purchase-orders" },
              { module: "Allocations", path: "/allocations" },
              { module: "Finance", path: "/finance" },
              { module: "Sales", path: "/sales" },
              { module: "HR Analysis", path: "/hr-analysis" },
              { module: "Project and Simulation", path: "/projection-simulation" },
              { module: "Settings", path: "/settings" },
              { module: "Uploads", path: "/uploads" },
            ];

            const firstAllowedPath =
              routePriority.find((r) => hasModulePermission(user, r.module, "read"))?.path ?? null;

            // No access: go to unauthorized.
            if (!firstAllowedPath) {
              return <Navigate to="/unauthorized" replace />;
            }

            // Only auto-redirect from generic landing routes (root).
            const isGenericLanding = location.pathname === "/" || location.pathname === "";
            if (isGenericLanding && location.pathname !== firstAllowedPath) {
              return <Navigate to={firstAllowedPath} replace />;
            }

            // Respect explicit URL navigation.
            return children;
          })()}
      </AuthenticatedTemplate>
    </>
  );
}

